<script>
    import { onMount, onDestroy } from 'svelte';
    import { commentsStore, commentThreadsStore } from '../../stores/commentsStore.js';
    import { commentAnchorService } from '../../services/commentAnchorService.js';
    
    let visible = false;
    let position = { x: 0, y: 0 };
    let selectedText = '';
    let documentType = null;
    
    function handleSelection() {
        const selection = window.getSelection();
        
        if (!selection || selection.isCollapsed || !selection.toString().trim()) {
            visible = false;
            return;
        }
        
        selectedText = selection.toString().trim();
        
        // Determine which editor contains the selection
        const specEditor = document.querySelector('.spec-editor');
        const claimsEditor = document.querySelector('.claims-editor');
        
        const range = selection.getRangeAt(0);
        const container = range.commonAncestorContainer;
        const element = container.nodeType === Node.TEXT_NODE ? container.parentElement : container;
        
        if (specEditor && specEditor.contains(element)) {
            documentType = 'spec';
        } else if (claimsEditor && claimsEditor.contains(element)) {
            documentType = 'claims';
        } else {
            // Selection is not in an editor
            visible = false;
            return;
        }
        
        // Get position for context menu
        const rect = range.getBoundingClientRect();
        position = {
            x: rect.left + (rect.width / 2),
            y: rect.top - 10
        };
        
        // Show context menu after a short delay
        setTimeout(() => {
            if (window.getSelection().toString().trim()) {
                visible = true;
            }
        }, 200);
    }
    
    function createComment() {
        if (!selectedText || !documentType) return;
        
        // Open comments panel
        commentsStore.open();
        
        // Create comment with anchor
        const commentId = `thread-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        let anchor = null;
        let snippet = selectedText.substring(0, 50) + (selectedText.length > 50 ? '...' : '');
        let position = 0;
        
        // Create anchor based on document type
        if (documentType === 'spec' && window.specEditor) {
            anchor = commentAnchorService.createSpecAnchor(window.specEditor, commentId);
            if (anchor) {
                position = anchor.startOffset;
            }
        } else if (documentType === 'claims' && window.claimsEditor) {
            anchor = commentAnchorService.createClaimsAnchor(window.claimsEditor, commentId);
            if (anchor) {
                position = anchor.startOffset;
            }
        }
        
        // Create the comment thread
        const thread = {
            id: commentId,
            text: '', // Empty for now - user will fill in the comment panel
            userId: window.currentUser?.id,
            userName: window.currentUser?.name || 'Anonymous',
            timestamp: new Date().toISOString(),
            documentType: documentType,
            position: position,
            snippet: snippet,
            anchorId: anchor?.id || null,
            resolved: false,
            replies: [],
            isNew: true // Flag to indicate this needs text
        };
        
        // Add to store and select it
        commentThreadsStore.addThread(thread);
        commentsStore.selectComment(commentId);
        
        // Hide context menu
        visible = false;
        
        // Clear selection
        window.getSelection().removeAllRanges();
    }
    
    function handleClickOutside(event) {
        if (visible && !event.target.closest('.comment-context-menu')) {
            visible = false;
        }
    }
    
    onMount(() => {
        document.addEventListener('mouseup', handleSelection);
        document.addEventListener('selectionchange', handleSelection);
        document.addEventListener('mousedown', handleClickOutside);
    });
    
    onDestroy(() => {
        document.removeEventListener('mouseup', handleSelection);
        document.removeEventListener('selectionchange', handleSelection);
        document.removeEventListener('mousedown', handleClickOutside);
    });
</script>

{#if visible}
    <div 
        class="comment-context-menu"
        style=" top: {position.y}px;left: {position.x}px;"
    >
        <button 
            class="comment-context-button"
            on:click={createComment}
            title="Add comment to selected text"
        >
            <i class="fas fa-comment"></i>
            Add Comment
        </button>
    </div>
{/if}

<style>
    .comment-context-menu {
        position: fixed;
        z-index: var(--z-index-popover);
        padding: 4px;
        background: var(--color-comments-header, #FEF3C7);
        border: 1px solid var(--color-comments-border, #FDE68A);
        border-radius: 6px;
        box-shadow: var(--shadow-md);
        transform: translate(-50%, -100%);
        animation: fadeIn 0.2s ease;
    }
    
    .comment-context-button {
        display: flex;
        align-items: center;
        padding: 6px 12px;
        font-size: 13px;
        font-weight: 500;
        white-space: nowrap;
        color: var(--color-comments-text, #92400E);
        background: none;
        border: none;
        border-radius: 4px;
        transition: background-color 0.2s;
        cursor: pointer;
        gap: 6px;
    }
    
    .comment-context-button:hover {
        background: var(--color-comments-hover, #FDE68A);
    }
    
    .comment-context-button i {
        font-size: 12px;
    }
    
    @keyframes fadeIn {
        from {
            opacity: 0;
            transform: translate(-50%, -90%);
        }

        to {
            opacity: 1;
            transform: translate(-50%, -100%);
        }
    }
    
    /* Dark mode */
    :global([data-theme="dark"]) .comment-context-menu {
        background: var(--color-surface-header-comments);
        border-color: var(--color-comments-border-dark, #92400E);
    }
    
    :global([data-theme="dark"]) .comment-context-button {
        color: var(--color-comments-text-dark, #FEF3C7);
    }
    
    :global([data-theme="dark"]) .comment-context-button:hover {
        background: var(--color-comments-hover-dark, #92400E);
    }
</style>